import { CourseModuleResponseDTO } from '@/jbi-shared/dto/jtms.dtos';
import { defaultApiState } from '@/store/types/general.types';
import { isDescendant } from '@/utils/jtms-utils';
import { AxiosError } from 'axios';
import {
  CourseModuleComponentItem,
  CourseModuleState,
  IndexBooleanPayload,
  UpdateCourseModulePayload,
  UpdateCourseModulePrerequisitePayload
} from './course-module.types';

export const courseModuleMutations = {
  /** API related mutations */
  CREATE_COURSE_MODULE_LOADING(state: CourseModuleState) {
    state.apiState.createCourseModulesApi = {
      ...defaultApiState,
      loading: true
    };
  },
  CREATE_COURSE_MODULE_SUCCESS(state: CourseModuleState) {
    state.apiState.createCourseModulesApi = {
      ...defaultApiState,
      success: true
    };
  },
  CREATE_COURSE_MODULE_ERROR(state: CourseModuleState, error: AxiosError) {
    state.apiState.createCourseModulesApi = {
      ...defaultApiState,
      error
    };
  },

  UPDATE_COURSE_MODULE_LOADING(state: CourseModuleState) {
    state.apiState.updateCourseModulesApi = {
      ...defaultApiState,
      loading: true
    };
  },
  UPDATE_COURSE_MODULE_SUCCESS(state: CourseModuleState) {
    state.apiState.updateCourseModulesApi = {
      ...defaultApiState,
      success: true
    };
  },
  UPDATE_COURSE_MODULE_ERROR(state: CourseModuleState, error: AxiosError) {
    state.apiState.updateCourseModulesApi = {
      ...defaultApiState,
      error
    };
  },

  DELETE_COURSE_MODULE_LOADING(state: CourseModuleState) {
    state.apiState.deleteCourseModulesApi = {
      ...defaultApiState,
      loading: true
    };
  },
  DELETE_COURSE_MODULE_SUCCESS(state: CourseModuleState) {
    state.apiState.deleteCourseModulesApi = {
      ...defaultApiState,
      success: true
    };
  },
  DELETE_COURSE_MODULE_ERROR(state: CourseModuleState, error: AxiosError) {
    state.apiState.deleteCourseModulesApi = {
      ...defaultApiState,
      error
    };
  },

  /** API related mutations */
  CLEAR_ACTIVE_COURSES_MODULES(state: CourseModuleState) {
    state.courseModulesTabState.activeCourseModules = [];
  },

  CLEAR_DELETED_COURSES_MODULES(state: CourseModuleState) {
    state.courseModulesTabState.deletedCourseModules = [];
  },

  ADD_ACTIVE_COURSE_MODULES(
    state: CourseModuleState,
    payload: CourseModuleComponentItem
  ) {
    state.courseModulesTabState.activeCourseModules.push(payload);
  },

  ADD_DELETED_COURSE_MODULES(
    state: CourseModuleState,
    payload: CourseModuleComponentItem
  ) {
    state.courseModulesTabState.deletedCourseModules.push(payload);
  },

  REMOVE_ACTIVE_COURSE_MODULES(state: CourseModuleState, index: number) {
    // Remove existing prerequisite associations
    const removedModuleTempId = state.courseModulesTabState.activeCourseModules[
      index
    ].tempId!;
    const activeModules = state.courseModulesTabState.activeCourseModules;

    activeModules.forEach((courseModule) => {
      // 1. Clean up the 'prerequisiteIds' array by reassigning it to a filtered version.
      if (courseModule.prerequisiteIds) {
        courseModule.prerequisiteIds = courseModule.prerequisiteIds.filter(
          (prereq) => prereq.id !== removedModuleTempId
        );
      }

      // 2. Clean up the 'requiredByIds' array by reassigning it to a filtered version.
      if (courseModule.requiredByIds) {
        courseModule.requiredByIds = courseModule.requiredByIds.filter(
          (reqId) => reqId !== removedModuleTempId
        );
      }
    });

    // Remove the selected active course module
    state.courseModulesTabState.activeCourseModules.splice(index, 1);
  },

  ASSIGN_EXISTING_COURSE_MODULES(
    state: CourseModuleState,
    payload: CourseModuleResponseDTO[]
  ) {
    // Convert payload into CourseModuleComponentItem
    state.courseModulesTabState.activeCourseModules = []; // Ensure course module array is emtpy

    payload.forEach((courseModule, index) => {
      const { prerequisites, ...remainingPayload } = courseModule;
      const convertedCourseComponentItem: CourseModuleComponentItem = {
        ...remainingPayload,
        requiredByIds: [], // Initialise these arrays as empty for now
        prerequisiteIds: courseModule.prerequisites?.map((prereq) => {
          return {
            id: prereq.id!,
            isExistingCourseModule: true
          };
        }),
        label: `Module ${index + 1}`,
        tempId: courseModule.id!, // Existing course modules have tempId set to existing ID
        hasFieldError: false, // Existing course modules have no field errors by default
        // Dropdown should be disabled only if there is only 1 course module
        shouldDisablePrerequisiteDropdown: payload.length === 1
      };

      state.courseModulesTabState.activeCourseModules.push(
        convertedCourseComponentItem
      );
    });
  },

  UPDATE_COURSE_MODULE(
    state: CourseModuleState,
    payload: UpdateCourseModulePayload
  ) {
    const { index, data } = payload;
    state.courseModulesTabState.activeCourseModules[index] = {
      ...state.courseModulesTabState.activeCourseModules[index],
      ...data
    };
  },

  /**
   * Manages prerequisite relationships between course modules using temporary IDs.
   *
   * Key concepts:
   * - tempId: Frontend-only identifier for tracking module relationships
   * - requiredBy: Array tracking which modules depend on this module
   * - prerequisites: Array of active course modules that must be completed before this module
   *
   * Example:
   * If Module A requires Module B as a prerequisite:
   * - Module A's `prerequisites` includes Module B's tempId
   * - Module B's `requiredBy` includes Module A's tempId
   * - Module B cannot select Module A as a prerequisite (prevents circular dependencies)
   *
   * Note: `tempId` and `requiredBy` are frontend-only and not persisted to database
   */
  UPDATE_COURSE_MODULE_PREREQUISITES(
    state: CourseModuleState,
    payload: UpdateCourseModulePrerequisitePayload
  ) {
    const { index, data: newPrerequisitesForTarget } = payload;

    // Get the tempId of the module being updated
    const updatedModuleTempId = state.courseModulesTabState.activeCourseModules[
      index
    ].tempId!;

    // Create a new updated array of all modules
    const newModulesArray = state.courseModulesTabState.activeCourseModules.map(
      (moduleInLoop) => {
        // 1. Determine the new 'prerequisiteIds' for the module in the loop
        let finalPrerequisiteIds = moduleInLoop.prerequisiteIds;
        // If this is the specific module we are updating, assign its new prerequisite list
        if (moduleInLoop.tempId === updatedModuleTempId) {
          finalPrerequisiteIds = newPrerequisitesForTarget;
        }

        // 2. Determine the new 'requiredByIds' for the module in the loop ---
        // Check if the module to update (identified by updatedModuleTempId) now requires
        // the module currently in the loop
        const isRequiredByUpdatedModule = newPrerequisitesForTarget.some(
          (prereq) => prereq.id === moduleInLoop.tempId
        );

        // Start with a fresh copy of the existing requiredByIds to avoid direct mutation
        let finalRequiredByIds = [...(moduleInLoop.requiredByIds || [])];
        const isAlreadyInList = finalRequiredByIds.includes(
          updatedModuleTempId
        );

        if (isRequiredByUpdatedModule) {
          // ADD logic: If it should be in the list but isn't, add it
          if (!isAlreadyInList) {
            finalRequiredByIds.push(updatedModuleTempId);
          }
        } else {
          // REMOVE logic: If it should NOT be in the list, filter it out
          if (isAlreadyInList) {
            finalRequiredByIds = finalRequiredByIds.filter(
              (reqId) => reqId !== updatedModuleTempId
            );
          }
        }

        // 3. Return a new object for the new array ---
        // This creates a fresh object with the updated properties
        return {
          ...moduleInLoop,
          prerequisiteIds: finalPrerequisiteIds,
          requiredByIds: finalRequiredByIds
        };
      }
    );

    // 4. Replace the old state array with the new one
    // This is a reactive assignment that Vue is guaranteed to detect, which will
    // cause all dependent getters and computed properties to re-evaluate
    state.courseModulesTabState.activeCourseModules = newModulesArray;
  },

  UPDATE_COURSE_MODULE_LABEL(state: CourseModuleState) {
    state.courseModulesTabState.activeCourseModules.forEach(
      (courseModule, index) => (courseModule.label = `Module ${index + 1}`)
    );
  },

  UPDATE_COURSE_MODULE_FIELD_ERROR(
    state: CourseModuleState,
    payload: IndexBooleanPayload
  ) {
    const { index, data } = payload;
    state.courseModulesTabState.activeCourseModules[index].hasFieldError = data;
  },

  UPDATE_COURSE_MODULE_PREREQUISITE_DROPDOWN_STATE(
    state: CourseModuleState,
    payload: IndexBooleanPayload
  ) {
    const { index, data } = payload;
    state.courseModulesTabState.activeCourseModules[
      index
    ].shouldDisablePrerequisiteDropdown = data;
  },

  UPDATE_COURSE_MODULE_FORM_ERROR(state: CourseModuleState, payload: boolean) {
    state.courseModulesTabState.courseModuleFormHasError = payload;
  },

  VALIDATE_PREREQUISITE_DROPDOWN_STATE(state: CourseModuleState) {
    const allCourseModules = state.courseModulesTabState.activeCourseModules;
    if (allCourseModules.length <= 1) {
      allCourseModules.forEach((module) => {
        module.shouldDisablePrerequisiteDropdown = true;
      });
      return; // Exit early
    }

    // Iterate over every active module to set its dropdown status
    allCourseModules.forEach((targetModule) => {
      const targetModuleTempId = targetModule.tempId!;
      // Find all modules that are valid prerequisites for the targetModule
      // This logic mirrors the 'getAvailablePrerequisitesForModule' getter.
      const availablePrerequisites = allCourseModules.filter(
        (potentialPrereq) => {
          const potentialPrereqTempId = potentialPrereq.tempId!;
          // Rule 1: A module cannot be its own prerequisite.
          if (potentialPrereq.tempId === targetModule.tempId) {
            return false;
          }
          // Rule 2: Apply advanced circular check on dependencies
          if (
            isDescendant(
              targetModuleTempId,
              potentialPrereqTempId,
              allCourseModules
            )
          ) {
            return false;
          }

          return true;
        }
      );

      // Disable the dropdown if the list of available options is empty.
      targetModule.shouldDisablePrerequisiteDropdown =
        availablePrerequisites.length === 0;
    });
  },

  VALIDATE_ALL_COURSE_MODULES(
    state: CourseModuleState,
    hasCourseModules: boolean
  ) {
    if (
      state.courseModulesTabState.activeCourseModules.length > 0 &&
      hasCourseModules
    ) {
      const hasEmptyFields: boolean = state.courseModulesTabState.activeCourseModules.some(
        (courseModule) =>
          !courseModule.module ||
          courseModule.trainers?.length === 0 ||
          !courseModule.startDate ||
          !courseModule.endDate
      );

      const hasFieldError: boolean = state.courseModulesTabState.activeCourseModules.some(
        (courseModule) => courseModule.hasFieldError
      );

      if (!hasFieldError && !hasEmptyFields) {
        state.courseModulesTabState.courseModuleFormHasError = false;
      }

      state.courseModulesTabState.courseModuleFormHasError =
        hasFieldError || hasEmptyFields;
    } else {
      state.courseModulesTabState.courseModuleFormHasError = false;
    }
  }
};
